import type { LucideIcon } from 'lucide-react'
import {
  BookOpen,
  Compass,
  GraduationCap,
  HeartHandshake,
  Landmark,
  MessageSquare,
  ScrollText,
  Sparkles,
  Users,
} from 'lucide-react'

export const CALENDLY_URL =
  'https://calendly.com/ashsingh/life-in-the-sikh-kingdoms'

export const TRAILER_EMBED_URL =
  'https://www.youtube.com/embed/2yMWvJinVgk?rel=0&modestbranding=1'

export const images = {
  hero: 'https://cdn.magicpatterns.com/uploads/7Y6dgrsg2zL5S5gqJcmvvX/Lahore_Fort_and_Hazuri_Bagh.png',
  venue:
    'https://uploadthingy.s3.us-west-1.amazonaws.com/kppHXez1XGBnRYW5bwUDVX/illumiarium.png',
  experience:
    'https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png',
  darbar:
    'https://cdn.magicpatterns.com/uploads/i8yqzvFJ6TFd6dTb6YJFfy/Lahor_Darbar.png',
  ranjitSingh:
    'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
  harmandirSahib:
    'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
  goldenThrone:
    'https://cdn.magicpatterns.com/uploads/itiARdFbZcdkmdrUEtYH65/Golden_Throne_of_Lahore.png',
  koiNoor:
    'https://cdn.magicpatterns.com/uploads/jyrMYyNM9JskmMzEnBrLTw/Koh-i-noor_Armlet.png',
  ash: 'https://cdn.magicpatterns.com/uploads/ak5taLmfmPnBbCtD49WXoj/image.png',
}

export const immersiveClipUrl =
  'https://cdn.magicpatterns.com/uploads/2vbujNeh6XCJnC6Xt6Yz87/animate_this_image_so_its_like.mp4'

export type ProofPoint = {
  value: string
  label: string
}

export const proofPoints: ProofPoint[] = [
  {
    value: '2000',
    label: 'Arts of the Sikh Kingdoms opens at the Royal Ontario Museum',
  },
  {
    value: 'No. 2',
    label: 'Best-attended exhibition in ROM history, ever since',
  },
  {
    value: '2026',
    label: 'The story returns, built for the first time as an immersive world',
  },
]

export type Pillar = {
  title: string
  body: string
  image: string
  icon: LucideIcon
}

export const experiencePillars: Pillar[] = [
  {
    title: 'Enter the world',
    body: 'Cinematic storytelling puts you inside 1799 Punjab, not in front of it.',
    image: images.darbar,
    icon: Compass,
  },
  {
    title: 'Meet the figures',
    body: 'Encounter the rulers, generals and moments that decided an empire.',
    image: images.ranjitSingh,
    icon: Users,
  },
  {
    title: 'Explore what is real',
    body: 'Authentic places and artefacts, researched and rendered with care.',
    image: images.harmandirSahib,
    icon: Landmark,
  },
  {
    title: 'Reflect on what it means',
    body: 'Courage, leadership, sovereignty and seva, felt rather than recited.',
    image: images.goldenThrone,
    icon: ScrollText,
  },
]

export type InvitationPoint = {
  title: string
  body: string
  icon: LucideIcon
}

export const invitationPoints: InvitationPoint[] = [
  {
    title: 'Influence',
    body: 'Your perspective shapes what gets built and how the story is told.',
    icon: Compass,
  },
  {
    title: 'Access',
    body: 'You see the work before anyone else does, at every stage.',
    icon: Sparkles,
  },
  {
    title: 'Community',
    body: 'You join a small circle of people building this with intention.',
    icon: HeartHandshake,
  },
  {
    title: 'Legacy',
    body: 'Your name is part of how this history returned to the world.',
    icon: ScrollText,
  },
]

export type Benefit = {
  title: string
  icon: LucideIcon
}

export const memberBenefits: Benefit[] = [
  {
    title: 'Early access to immersive experiences as they open',
    icon: Sparkles,
  },
  {
    title: 'Invitations to private previews and founding gatherings',
    icon: Users,
  },
  {
    title: 'Direct conversations with the founding team',
    icon: MessageSquare,
  },
  {
    title: 'A real say in how the experience takes shape',
    icon: Compass,
  },
  {
    title: 'Behind-the-scenes access to progress and research',
    icon: BookOpen,
  },
  {
    title: 'Recognition as a Founding Member of Sikh Kingdoms',
    icon: ScrollText,
  },
  {
    title: 'Preferential access to future releases and events',
    icon: Landmark,
  },
  {
    title: 'Membership in a values-aligned founding community',
    icon: HeartHandshake,
  },
]

export type AudienceItem = {
  title: string
}

export const audienceItems: AudienceItem[] = [
  { title: 'People passionate about Sikh history and heritage' },
  { title: 'Parents and grandparents thinking about the next generation' },
  { title: 'Educators, storytellers, technologists and community builders' },
  { title: 'Patrons who want to support meaningful cultural innovation' },
  { title: 'People seeking an active role in preserving and sharing Sikh legacy' },
]

export type PreviewItem = {
  label: string
  caption: string
  image: string
}

export const previewItems: PreviewItem[] = [
  {
    label: 'Concept art',
    caption: 'An early rendering of the Lahore Darbar, reimagined in full scale.',
    image: images.darbar,
  },
  {
    label: 'Concept art',
    caption: 'Maharaja Ranjit Singh, one of the figures you will encounter.',
    image: images.ranjitSingh,
  },
  {
    label: 'Research reference',
    caption: 'The Golden Throne of Lahore, one of the real artefacts guiding the work.',
    image: images.goldenThrone,
  },
]

export type TrustFact = {
  title: string
  body: string
  icon: LucideIcon
}

export const trustFacts: TrustFact[] = [
  {
    title: 'Kanwar Singh Dhillon',
    body: 'The renowned Art of Punjab visual artist leads creative direction, bringing defining moments of Sikh history to life.',
    icon: BookOpen,
  },
  {
    title: 'Academy Award-winning animators',
    body: 'Artists behind The Lord of the Rings bring cinematic storytelling craft to the world of the Sikh Kingdoms.',
    icon: Sparkles,
  },
  {
    title: 'Emmy-winning artists',
    body: 'Creative talent behind global immersive installations brings deep experience in building environments at scale.',
    icon: GraduationCap,
  },
  {
    title: 'Historical integrity',
    body: 'Every figure, place and object begins with real history, then receives consultant review before it becomes cinematic.',
    icon: Landmark,
  },
]

export type Step = {
  title: string
  body: string
}

export const callSteps: Step[] = [
  {
    title: 'Choose a time',
    body: 'Pick whatever works for your schedule, in a couple of clicks.',
  },
  {
    title: 'Have the conversation',
    body: 'A relaxed 20 minutes with Ash about the vision, and where you might fit.',
  },
  {
    title: 'Decide, on your terms',
    body: 'Founding membership only if it feels right. No pressure, no pitch deck ambush.',
  },
]

export type Faq = {
  question: string
  answer: string
}

export const faqs: Faq[] = [
  {
    question: 'What is Sikh Kingdoms?',
    answer:
      'An immersive experience bringing the people, places and decisions of the Sikh Empire to life through cinematic storytelling, set to open at Illuminarium Toronto.',
  },
  {
    question: 'What does a Founding Member do?',
    answer:
      'You help shape the project at its earliest stage, through your perspective, your network and, for many, your support. It is participation, not a passive purchase.',
  },
  {
    question: 'How much does membership cost?',
    answer:
      'There is no fixed public price. We talk through what makes sense for you on the call itself, based on how you want to be involved.',
  },
  {
    question: 'Is this a donation or a membership?',
    answer:
      'It can be either, or both. Some Founding Members contribute financially, others contribute expertise, introductions or time. All are valued the same way.',
  },
  {
    question: 'What stage is the project currently at?',
    answer:
      'In active development. The story, research and design are underway, and the first immersive experiences are being built toward a 2026 opening.',
  },
  {
    question: 'When will experiences become available?',
    answer:
      'Founding Members get early previews well before the public opening in 2026.',
  },
  {
    question: 'How much time must members contribute?',
    answer:
      'As much or as little as you choose. Some join a quarterly call, others get more involved. There is no minimum commitment.',
  },
  {
    question: 'Can people outside the Sikh community join?',
    answer:
      'Yes. This history belongs to everyone who believes it deserves to be experienced properly, not only to those raised inside it.',
  },
  {
    question: 'How will historical and cultural accuracy be protected?',
    answer:
      'Historical consultants review every figure, place and narrative before it is built, so the story stays rooted in fact.',
  },
  {
    question: 'What happens after I book a call?',
    answer:
      'You will hear from Ash directly to confirm the time. The call itself is a conversation, nothing is decided or asked of you in advance.',
  },
]
